<template>
<box-view title="Danh sách sản phẩm" class="product-list " @close="$router.back()">
    <div class="container-  ">
        <!-- TOOLBAR -->
        <div class="card mb-2 p-2">
            <div class="row g-2 align-items-center">
                <div class="col-md-4">
                    <input v-model="searchQuery" type="text" class="form-control form-control-sm" placeholder="Tìm tên / mã sản phẩm" />
                </div>

                <div class="col-md-3">
                    <button v-if="canManageProducts" class="btn btn-sm btn-success" @click="openCreate">
                        <i class="fas fa-plus"></i> Thêm sản phẩm
                    </button>
                    <button class="btn btn-sm btn-warning ms-1" @click="$router.push({ name: 'importhistory' })">
                         <i class="fas fa-history"></i> Lịch sử nhập kho
                    </button>
                </div>
            </div>
        </div>

        <!-- PRODUCT TABLE -->
        <div class="table-responsive">
            <table class="table table-sm table-bordered align-middle">
                <thead class="table-info">
                    <tr>
                        <th width="50">#</th>
                        <th width="70">Ảnh</th>
                        <th>Sản phẩm</th>
                        <th>Mã</th>
                        <th>Đơn vị</th>
                        <th>Giá bán</th>
                        <th>Giá vốn</th>
                        <th>Trạng thái</th>
                        <th>Danh mục</th>
                        <th>Tồn kho</th>
                        <th width="120" v-if="canManageProducts"></th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="(p, i) in paginatedProducts" :key="p.id">
                        <td>{{ (currentPage - 1) * pageSize + i + 1 }}</td>

                        <td>
                            <img :src="p.image || ''" class="product-img" @error="imgError" />
                        </td>

                        <td class="fw-bold">{{ p.name }}</td>
                        <td>{{ p.code }}</td>
                        <td>{{ p.unit }}</td>

                        <td>
                            <span v-if="p.price > 0">
                                {{ p.price | formatCurrency }}
                            </span>
                            <span v-else class="text-danger">
                                Chưa có giá
                            </span>
                        </td>

                        <td>
                            <span v-if="p.costPrice > 0">
                                {{ p.costPrice | formatCurrency }}
                            </span>
                            <span v-else class="text-danger">
                                Chưa có giá vốn
                            </span>
                        </td>

                        <td>
                            <span :class="{'text-danger': p.status === 'inactive'}">
                                {{ getStatus(p.status) }}
                            </span>
                        </td>

                        <td>{{ getCategoryName(p.category) }}</td>

                        <td>
                            <span :class="{'text-danger': p.stock <= 0}">{{ p.stock | formatNumber }}</span>
                        </td>

                        <td class="text-center" v-if="canManageProducts">
                            <button class="btn btn-sm btn-primary me-1" @click="openEdit(p)">
                                <i class="fas fa-edit"></i>
                            </button>

                            <button class="btn btn-sm btn-warning" @click="openImport(p)">
                                <i class="fas fa-warehouse"></i>
                            </button>

                            <button class="btn btn-sm btn-danger ms-1" @click="confirmDelete(p)">
                                <i class="fas fa-trash"></i>
                            </button>
                        </td>
                    </tr>

                    <tr v-if="filteredProducts.length === 0">
                        <td colspan="11" class="text-center text-muted">Không có sản phẩm</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- PAGINATION -->
        <nav v-if="totalPages > 1" class="mt-2">
            <ul class="pagination pagination-sm justify-content-center">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                    <a class="page-link" href="#" @click.prevent="currentPage--">«</a>
                </li>
                <li v-for="page in totalPages" :key="page" class="page-item" :class="{ active: page === currentPage }">
                    <a class="page-link" href="#" @click.prevent="currentPage = page">{{ page }}</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                    <a class="page-link" href="#" @click.prevent="currentPage++">»</a>
                </li>
            </ul>
        </nav>

        <!-- CREATE / EDIT POPUP -->
        <div v-if="showForm" class="modal-backdrop-custom">
            <div class="modal-custom">
                <div class="modal-header bg-primary text-white">
                    <h6 class="mb-0">
                        {{ editing ? 'Cập nhật sản phẩm' : 'Thêm sản phẩm' }}
                    </h6>
                    <button class="btn btn-sm btn-danger" @click="closeForm">
                        ✕
                    </button>
                </div>

                <div class="modal-body">
                    <div class="mb-2">
                        <label class="form-label d-flex">Tên sản phẩm</label>
                        <input v-model="form.name" class="form-control form-control-sm" />
                    </div>

                    <div class="mb-2">
                        <label class="form-label d-flex">Mã sản phẩm</label>
                        <input v-model="form.code" class="form-control form-control-sm" />
                    </div>

                    <div class="mb-2">
                        <label class="form-label d-flex">Đơn vị</label>
                        <input v-model="form.unit" class="form-control form-control-sm" />
                    </div>

                    <div class="mb-2">
                        <label class="form-label d-flex">Giá bán</label>
                        <money-input v-model="form.price" class="form-control form-control-sm" />
                    </div>

                    <div class="mb-2">
                        <label class="form-label d-flex">Giá vốn</label>
                        <money-input v-model="form.costPrice" class="form-control form-control-sm" />
                    </div>

                    <div class="mb-2">
                        <label class="form-label d-flex">Trạng thái</label>
                        <select v-model="form.status" class="form-control form-control-sm">
                            <option value="active">Còn kinh doanh</option>
                            <option value="inactive">Ngưng kinh doanh</option>
                        </select>
                    </div>

                    <div class="mb-2">
                        <label class="form-label d-flex">Danh mục</label>
                        <select v-model="form.category" class="form-control form-control-sm">
                            <option v-for="category in categories" :key="category.id" :value="category.id">
                                {{ category.name }}
                            </option>
                        </select>
                    </div>  
                    <div class="mb-2">
                        <label class="form-label d-flex">Nhà cung cấp</label>
                         <select v-model="form.supplierId" class="form-control form-control-sm">
                            <option value="">-- Chọn nhà cung cấp --</option>
                            <option v-for="sup in suppliers" :key="sup.id" :value="sup.id">
                                {{ sup.name }}
                            </option>
                        </select>
                    </div>

                    <div class="mb-2">
                        <label class="form-label d-flex">Ảnh sản phẩm</label>
                        <input type="file" class="form-control form-control-sm" @change="onUpload" />
                    </div>
                </div>

                <div class="modal-footer">
                    <button class="btn btn-sm btn-success" @click="saveProduct">
                        Lưu
                    </button>
                </div>
            </div>
        </div>

        <!-- IMPORT STOCK -->
        <div v-if="showImport" class="modal-backdrop-custom">
            <div class="modal-custom">
                <div class="modal-header bg-warning">
                    <h6 class="mb-0">Nhập kho</h6>
                    <button class="btn btn-sm btn-danger" @click="showImport = false">
                        ✕
                    </button>
                </div>

                <div class="modal-body">
                    <div class="mb-2">
                        <b>{{ selectedProduct.name }}</b>
                    </div>

                    <div class="mb-2">
                        <label class="form-label d-flex">Nhập số lượng</label>
                        <money-input v-model="importQty" class="form-control form-control-sm" placeholder="Số lượng nhập" />
                    </div>
                    <div class="mb-2">
                        <label class="form-label d-flex">Giá vốn mới</label>
                        <money-input v-model="importCostPrice" class="form-control form-control-sm" placeholder="Giá vốn nhập lô này" />
                        <small class="text-muted">Hệ thống sẽ cập nhật giá vốn sản phẩm theo giá này.</small>
                    </div>
                    <div class="mb-2 p-2 bg-light border rounded">
                        <div class="d-flex justify-content-between align-items-center">
                            <span class="text-muted">Thành tiền:</span>
                            <span class="fw-bold text-danger fs-5">{{ (importQty * importCostPrice) | formatCurrency }}</span>
                        </div>
                    </div>
                    <div class="mb-2">
                        <label class="form-label d-flex">Ngày nhập</label>
                        <input type="text" class="form-control form-control-sm" :value="new Date().toLocaleString()" readonly />
                    </div>

                    <div class="mb-2">
                         <label class="form-label d-flex">Nhà cung cấp</label>
                         <select v-model="importSupplierId" class="form-control form-control-sm">
                            <option value="">-- Chọn nhà cung cấp --</option>
                             <option v-for="sup in suppliers" :key="sup.id" :value="sup.id">
                                {{ sup.name }}
                            </option>
                        </select>
                    </div>

                    <div class="mb-2">
                        <label class="form-label d-flex">Người nhập</label>
                        <input :value="user.name" type="text" class="form-control form-control-sm" readonly />
                    </div>

                    <div class="mb-2">
                        <label class="form-label d-flex">Ghi chú</label>
                        <input v-model="note" type="text" class="form-control form-control-sm" placeholder="Ghi chú nhập kho" />
                    </div>
                </div>

                <div class="modal-footer">
                    <button class="btn btn-sm btn-success" @click="importStock">
                        Xác nhận
                    </button>
                </div>
            </div>
        </div>
    </div>

</box-view>
</template>

<script>
export default {
    name: 'ProductList',
    props: {
        products: {
            type: Array,
            default: () => []
        },
        categories: {
            type: Array,
            default: () => []
        },
        user: {
            type: Object,
            default: () => ({})
        },
        suppliers: {
            type: Array,
            default: () => []
        },
        settings: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            searchQuery: '',
            showForm: false,
            showImport: false,
            editing: false,
            importQty: 0,
            importCostPrice: 0,
            selectedProduct: null,
            note: '',
            currentPage: 1,
            pageSize: 30,
            
            form: {
                id: null,
                name: '',
                code: '',
                price: 0,
                costPrice: 0, // Giá vốn
                unit: '',
                image: '',
                stock: 0,
                status: 'active', // Trạng thái sản phẩm
                category: '', // Danh mục sản phẩm
                supplierId: '', // Default product supplier
            },
            importSupplierId: '', // For import modal
        };
    },

    computed: {
        canManageProducts() {
            const role = this.user.role || 'admin';
            if(role === 'admin') return true;
            const roleConfig = this.settings.user?.roles?.[role];
            return roleConfig?.permissions?.includes('manageProducts');
        },

        filteredProducts() {
            const query = this.searchQuery.toLowerCase().trim();
            if (!query) return this.products;

            return this.products.filter(p => {
                return (
                    (p.name && p.name.toLowerCase().includes(query)) ||
                    (p.code && p.code.toLowerCase().includes(query))
                );
            });
        },
        totalPages() {
            return Math.ceil(this.filteredProducts.length / this.pageSize);
        },
        paginatedProducts() {
            const start = (this.currentPage - 1) * this.pageSize;
            return this.filteredProducts.slice(start, start + this.pageSize);
        }
    },

    methods: {
        imgError(e) {
            e.target.onerror = null;
            e.target.src = '';
        },

        getStatus(status) {
            return status === 'active' ? 'Còn kinh doanh' : 'Ngưng kinh doanh';
        },

        getCategoryName(categoryId) {
            const category = this.categories.find(c => c.id === categoryId);
            return category ? category.name : 'Chưa xác định';
        },

        openCreate() {
            this.editing = false;
            this.form = {
                id: null,
                name: '',
                code: 'SP' + Date.now().toString().slice(-6),
                price: 0,
                costPrice: 0,
                unit: '',
                image: '',
                stock: 0,
                status: 'active',
                category: '',
                supplierId: '',
            };
            this.showForm = true;
        },

        openEdit(p) {
            this.editing = true;
            this.form = {
                ...p
            };
            this.showForm = true;
        },

        closeForm() {
            this.showForm = false;
        },

        saveProduct() {
            const formData = new FormData();
            if (this.form.id) formData.append('id', this.form.id);
            formData.append('name', this.form.name);
            formData.append('code', this.form.code);
            formData.append('sale_price', this.form.price);
            formData.append('cost_price', this.form.costPrice);
            formData.append('unit', this.form.unit);
            formData.append('stock_quantity', this.form.stock);
            formData.append('category_id', this.form.category);
            formData.append('status', this.form.status);
            
             if (this.form.image_base64) {
                 formData.append('image_base64', this.form.image_base64);
             }

            this.$root.api.post('logach/postSaveLoGachProduct', formData).then(res => {
                if (res.data.status === 'OK') {
                    this.$root.notify(this.editing ? 'Đã cập nhật' : 'Đã thêm mới', 'success');
                    this.showForm = false;
                    this.$emit('refresh-global');
                } else {
                    this.$root.showError(res.data.message);
                }
            });
        },

        confirmDelete(p) {
            if (confirm('Bạn có chắc muốn xóa sản phẩm ' + p.name + '?')) {
                this.$root.api.post('logach/postDeleteLoGachProduct', { id: p.id }).then(res => {
                     if (res.data.status === 'OK') {
                         this.$root.notify('Đã xóa', 'success');
                         this.$emit('refresh-global');
                     } else {
                         this.$root.showError(res.data.message);
                     }
                });
            }
        },

        onUpload(e) {
            const file = e.target.files[0];
            if (!file) return;
            this.form.image = URL.createObjectURL(file);
            
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                this.form.image_base64 = reader.result;
            };
        },

        openImport(p) {
            this.selectedProduct = p;
            this.importQty = 0;
            this.importCostPrice = p.costPrice || 0;
            this.importSupplierId = p.supplierId || ''; 
            this.showImport = true;
        },

        importStock() {
            if (this.importQty > 0) {
                 this.$root.api.post('logach/postImportLoGachStock', {
                    product_id: this.selectedProduct.id,
                    quantity: this.importQty,
                    cost_price: this.importCostPrice,
                    supplier_id: this.importSupplierId,
                    user_id: this.user.id || 0,
                    note: this.note
                }).then(res => {
                    if (res.data.status === 'OK') {
                        this.$root.notify('Nhập kho thành công', 'success');
                        this.showImport = false;
                        this.$emit('refresh-global');
                    } else {
                         this.$root.showError(res.data.message);
                    }
                });
            } else {
                this.$root.notify('Số lượng phải lớn hơn 0', 'error');
            }
        }
    }
};
</script>

<style scoped>
.product-img {
    width: 60px;
    height: 60px;
    object-fit: contain;
    border-radius: 4px;
    border: 1px solid #ddd;
}
.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.modal-custom {
  background-color: white;
  border-radius: 5px;
  width: 500px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  padding: 15px;
  overflow-y: auto;
}

.modal-footer {
  padding: 10px 15px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #eee;
}
</style>
